import { useTranslation } from 'react-i18next'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material'
import { LoadingButton } from '@mui/lab'

import TextInputController from '@/components/ui/inputs/TextInputController'
import { useCreateAdminMutation } from '@/core/services/super-admin'
import { KeycloakUserType } from '@/core/types/shared'
import { useSnackbar } from 'notistack'
import { getApiErrorMessage } from '@/utils/errors'

const formSchema = yup.object().shape({
  email: yup.string().email().required(),
  name: yup.string().required(),
  surname: yup.string().required(),
})

export type CreateAdminFormData = yup.InferType<typeof formSchema>

export type CreateAdminDialogProps = {
  establishmentId: string
  onClose?: () => void
  open: boolean
}

export function CreateAdminDialog(props: CreateAdminDialogProps) {
  const { establishmentId, onClose, open } = props
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [createAdmin, { isLoading: isCreatingAdmin }] = useCreateAdminMutation()

  const rhForm = useForm<CreateAdminFormData>({ defaultValues: { email: '', name: '', surname: '' }, resolver: yupResolver(formSchema) })

  const onCreateAdmin: SubmitHandler<CreateAdminFormData> = async (data) => {
    try {
      await createAdmin({
        userInfo: {
          name: data.name,
          surname: data.surname,
        },
        email: data.email,
        establishment: establishmentId,
        type: KeycloakUserType.ADMIN,
      }).unwrap()
      enqueueSnackbar(t('notification.admin.create.success'), { variant: 'success' })
      onClose?.()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.admin.create.error')), { variant: 'error' })
    }
  }

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>{t('page.superAdmin.createAdminDialog.title')}</DialogTitle>
      <form onSubmit={rhForm.handleSubmit(onCreateAdmin)}>
        <DialogContent sx={{ minWidth: '500px', minHeight: '200px' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextInputController control={rhForm.control} fullWidth label={t('field.firstName.label')} name="name" id="name" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInputController control={rhForm.control} fullWidth label={t('field.lastName.label')} name="surname" id="surname" />
            </Grid>
            <Grid item xs={12}>
              <TextInputController control={rhForm.control} fullWidth label={t('field.email.label')} name="email" id="email" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={onClose}>
            {t('common:button.cancel.label')}
          </Button>
          <LoadingButton loading={isCreatingAdmin} type="submit" variant="contained" id="create-admin-button">
            {t('common:button.submit.label')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default CreateAdminDialog
